if (typeof SellingPlanSelector !== 'function') {
  class SellingPlanSelector extends HTMLElement {
    constructor() {
      super();
      this.sellingPlanGroups = JSON.parse(this.querySelector('[data-selling-plan-groups]')?.textContent || '[]');
      this.requiresSellingPlan = this.hasAttribute('data-requires-selling-plan');
      this.currentSellingPlanId = '';
      this.onetimeVariantId = null;
      
      this.init();
    }

    init() {
      // Set up event listener for plan selection
      this.addEventListener('change', this.onPlanChange.bind(this));
      
      // Listen for variant changes from the product-variants component
      const productVariants = this.closest('[data-js-product-component]')?.querySelector('product-variants');
      if (productVariants) {
        productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChange.bind(this));
        productVariants.addEventListener('change', this.onVariantChange.bind(this));
      }

      // Initialize with default selection if there's a default plan
      //this.setInitialSelection();
      
      // Setup the selling plan options
      this.setupPlanOptions();

      // Sync internal state with the initially checked option
      this.syncInitialSelection();
    }
    
    setInitialSelection() {
      // Select one-time purchase by default unless product requires a selling plan
      if (!this.requiresSellingPlan) {
        const onetimeOption = this.querySelector('[data-onetime-purchase]');
        if (onetimeOption) {
          onetimeOption.checked = true;
          this.currentSellingPlanId = '';
          if (onetimeOption.hasAttribute('data-variant-id')) {
            this.onetimeVariantId = onetimeOption.getAttribute('data-variant-id');
          }
        }
      } else if (this.sellingPlanGroups.length > 0 && this.sellingPlanGroups[0].selling_plans.length > 0) {
        // If product requires selling plan, select the first plan
        const firstPlan = this.sellingPlanGroups[0].selling_plans[0];
        const firstPlanOption = this.querySelector(`[data-selling-plan-id="${firstPlan.id}"]`);
        if (firstPlanOption) {
          firstPlanOption.checked = true;
          this.currentSellingPlanId = firstPlan.id;
        }
      }
      
      this.updateHiddenInput();
    }
    
    setupPlanOptions() {
      // Add click event listeners to all options
      this.querySelectorAll('.selling-plan__item input').forEach(input => {
        input.addEventListener('click', () => {
          this.selectPlan(input);
        });
      });
    }

    syncInitialSelection() {
      const checkedOption = this.querySelector('.selling-plan__item input:checked');

      if (checkedOption) {
        this.selectPlan(checkedOption);
        return;
      }

      // Fallback to deterministic defaults when no input is checked
      if (!this.requiresSellingPlan) {
        const onetimeOption = this.querySelector('[data-onetime-purchase]');
        if (onetimeOption) {
          onetimeOption.checked = true;
          this.selectPlan(onetimeOption);
          return;
        }
      }

      const firstPlanOption = this.querySelector('[data-selling-plan-id]');
      if (firstPlanOption) {
        firstPlanOption.checked = true;
        this.selectPlan(firstPlanOption);
      } else {
        this.currentSellingPlanId = '';
        this.onetimeVariantId = null;
        this.updateHiddenInput();
      }
    }
    
    selectPlan(input) {
      // Update selected state for all options
      this.querySelectorAll('[data-selling-plan-id]').forEach(opt => {
        opt.removeAttribute('data-selected');
        if (opt.parentElement) {
          opt.parentElement.classList.remove('active');
        }
      });
      
      // Set selected state on the chosen option
      input.setAttribute('data-selected', '');
      if (input.parentElement) {
        input.parentElement.classList.add('active');
      }
      
      // Update current selling plan ID
      this.currentSellingPlanId = input.getAttribute('data-selling-plan-id');
      
      // Track one-time purchase variant ID separately
      if (input.hasAttribute('data-onetime-purchase') && input.hasAttribute('data-variant-id')) {
        this.onetimeVariantId = input.getAttribute('data-variant-id');
      } else {
        this.onetimeVariantId = null;
      }
      
      // Update hidden input for form submission
      this.updateHiddenInput();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('SELLING_PLAN_CHANGE', {
        detail: {
          sellingPlanId: this.currentSellingPlanId,
          variantId: this.onetimeVariantId || this.getVariantId(this.currentSellingPlanId)
        }
      }));
    }
    
    onPlanChange(event) {
      const target = event.target;
      if (target.hasAttribute('data-selling-plan-id')) {
        this.selectPlan(target);
      }
    }

    onVariantChange() {
      // Keep form payload aligned with currently checked purchase option
      const checkedOption = this.querySelector('.selling-plan__item input:checked');
      if (checkedOption) {
        this.selectPlan(checkedOption);
      } else {
        this.syncInitialSelection();
      }
    }
    
    updateHiddenInput() {
      // Update the hidden input field for the selling plan in this component
      const hiddenInput = this.querySelector('input[name="selling_plan"]');
      if (hiddenInput && this.currentSellingPlanId) {
        hiddenInput.value = this.currentSellingPlanId;
      } else if (hiddenInput) {
        hiddenInput.value = '';
      }
      
      // Also update the hidden input in the product form if it exists
      const productForm = this.closest('[data-js-product-component]')?.querySelector('product-form form');
      if (productForm) {
        let formSellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
        if (!formSellingPlanInput) {
          formSellingPlanInput = document.createElement('input');
          formSellingPlanInput.type = 'hidden';
          formSellingPlanInput.name = 'selling_plan';
          productForm.appendChild(formSellingPlanInput);
        }
        formSellingPlanInput.value = this.currentSellingPlanId || '';
      }

      const variantIdInputs = this.closest('[data-js-product-component]')?.querySelectorAll('product-form form input[name="id"]');
      const resolvedVariantId = this.onetimeVariantId || this.getVariantId(this.currentSellingPlanId);
      if (variantIdInputs && resolvedVariantId) {
        variantIdInputs.forEach(input => {
          input.value = resolvedVariantId;
        });
      }
    }

    getVariantId(sellingPlanId) {
        if (!sellingPlanId) {
            return this.onetimeVariantId || null;
        }
        
        // Parse the product JSON to find the variant with this selling plan ID
        const productJSON = this.sellingPlanGroups;
        
        // Find a variant that has the selling plan allocation
        const matchingVariant = productJSON.variants?.find(variant => 
            variant.selling_plan_allocations?.some(allocation => 
                allocation.selling_plan_id.toString() === sellingPlanId.toString()
            )
        );
        
        return matchingVariant?.id || null;
    }
  }

  if (typeof customElements.get('selling-plan-selector') == 'undefined') {
    customElements.define('selling-plan-selector', SellingPlanSelector);
  }
} 